import { useCallback, useEffect, useRef, useState } from "react";
import { timerTick } from "../lib/haptic";

interface Props {
  /** Card id — used to re-key local state per card. */
  cardId: string;
  durationSec: number;
}

/**
 * §3.8 Mini-challenge timer.
 * - Read durationSec from the card.
 * - Local UI state, re-keyed on (cardId, durationSec).
 * - Tap → toggle start/pause.
 * - Long-press → reset.
 * - One-shot navigator.vibrate(20) on expiration.
 * - Tap on expired timer is a NO-OP. Only long-press revives.
 * - Timer does NOT auto-resolve the card.
 */
export function MiniChallengeTimer({ cardId, durationSec }: Props) {
  const [remaining, setRemaining] = useState<number>(durationSec);
  const [running, setRunning] = useState(false);
  const expiredRef = useRef(false);
  const lastTickRef = useRef<number | null>(null);
  /** 3-2-1 count-in before the first start — builds the room up. */
  const [countIn, setCountIn] = useState<number | null>(null);
  const countInTimer = useRef<number | null>(null);

  const clearCountIn = useCallback(() => {
    if (countInTimer.current) {
      clearInterval(countInTimer.current);
      countInTimer.current = null;
    }
    setCountIn(null);
  }, []);

  // Re-key local state on card change.
  useEffect(() => {
    setRemaining(durationSec);
    setRunning(false);
    expiredRef.current = false;
    lastTickRef.current = null;
    clearCountIn();
  }, [cardId, durationSec, clearCountIn]);

  // Clear the interval on unmount.
  useEffect(() => clearCountIn, [clearCountIn]);

  // Tick.
  useEffect(() => {
    if (!running) return;
    let raf = 0;
    function step(t: number) {
      const last = lastTickRef.current ?? t;
      const dt = (t - last) / 1000;
      lastTickRef.current = t;
      setRemaining((r) => {
        const next = Math.max(0, r - dt);
        if (next === 0 && !expiredRef.current) {
          expiredRef.current = true;
          if ("vibrate" in navigator) {
            try {
              (navigator as Navigator).vibrate?.(20);
            } catch {
              /* ignore */
            }
          }
          setRunning(false);
        }
        return next;
      });
      raf = requestAnimationFrame(step);
    }
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [running]);

  // Long-press handling.
  const longPressTimer = useRef<number | null>(null);
  const longPressFired = useRef(false);

  const onPointerDown = useCallback(() => {
    longPressFired.current = false;
    // (long-press below also cancels an active count-in)
    longPressTimer.current = window.setTimeout(() => {
      longPressFired.current = true;
      // Reset
      setRemaining(durationSec);
      setRunning(false);
      expiredRef.current = false;
      lastTickRef.current = null;
      clearCountIn();
      if ("vibrate" in navigator) {
        try {
          (navigator as Navigator).vibrate?.(15);
        } catch {
          /* ignore */
        }
      }
    }, 500);
  }, [durationSec, clearCountIn]);

  const cancelLongPress = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  const onPointerUp = useCallback(() => {
    cancelLongPress();
    if (longPressFired.current) return;
    if (expiredRef.current) return; // tap on expired = no-op
    // Tap during count-in = skip straight to running (impatient host wins).
    if (countInTimer.current) {
      clearCountIn();
      lastTickRef.current = null;
      setRunning(true);
      return;
    }
    // Fresh timer → 3-2-1 count-in first (Kahoot-style build-up). Resume
    // after pause starts instantly — the drama beat is for the first start.
    setRunning((r) => {
      if (!r && remaining === durationSec) {
        let n = 3;
        setCountIn(3);
        timerTick();
        countInTimer.current = window.setInterval(() => {
          n -= 1;
          if (n <= 0) {
            clearCountIn();
            lastTickRef.current = null;
            setRunning(true);
          } else {
            setCountIn(n);
            timerTick();
          }
        }, 550);
        return false;
      }
      lastTickRef.current = null;
      return !r;
    });
  }, [cancelLongPress, clearCountIn, remaining, durationSec]);

  const minutes = Math.floor(remaining / 60);
  const seconds = Math.floor(remaining % 60);
  const pct = durationSec > 0 ? remaining / durationSec : 0;

  return (
    <div
      className="select-none touch-none w-full flex flex-col items-center justify-center"
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
      onPointerLeave={cancelLongPress}
      onPointerCancel={cancelLongPress}
    >
      <div className="relative w-56 h-56 sm:w-64 sm:h-64">
        <svg viewBox="0 0 100 100" className="absolute inset-0 -rotate-90">
          <circle cx="50" cy="50" r="46" stroke="white" strokeOpacity="0.12" strokeWidth="5" fill="none" />
          <circle
            cx="50"
            cy="50"
            r="46"
            stroke="white"
            strokeWidth="5"
            fill="none"
            strokeDasharray={`${2 * Math.PI * 46}`}
            strokeDashoffset={`${2 * Math.PI * 46 * (1 - pct)}`}
            strokeLinecap="round"
            style={{ transition: "stroke-dashoffset 100ms linear" }}
          />
        </svg>
        {countIn != null ? (
          /* 3-2-1 build-up — big number pops per beat (key re-mounts it). */
          <div className="absolute inset-0 flex items-center justify-center">
            <div
              key={countIn}
              className="font-display text-8xl font-black tabular-nums animate-phase-pop"
              style={{
                color: "var(--accent, #fff)",
                textShadow: "0 0 24px var(--accent, rgba(255,255,255,0.6))",
              }}
            >
              {countIn}
            </div>
          </div>
        ) : (
          <div
            className={`absolute inset-0 flex flex-col items-center justify-center ${
              running ? "animate-pickPulse" : ""
            }`}
          >
            <div className="font-display text-6xl font-black tabular-nums">
              {minutes > 0
                ? `${minutes}:${seconds.toString().padStart(2, "0")}`
                : seconds.toString().padStart(2, "0")}
            </div>
            <div className="text-xs opacity-60 mt-1">
              {expiredRef.current ? "—" : running ? "▶" : "⏸"}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
